import { useState, useRef } from "react";
import { ShieldCheck, Loader2, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { DISCLAIMER_PARAGRAPHS } from "@/lib/disclaimer";

// Clickwrap acknowledgment gate — blocks the app until the user reads + agrees.
export default function DisclaimerGate() {
  const { user, setUser } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [agree, setAgree] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  if (!user || user.disclaimer_accepted) return null;

  const onScroll = () => {
    const el = scrollRef.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setScrolledToEnd(true);
  };

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/legal/accept");
      setUser(data.user);
      toast.success("Thanks — you're all set. Learn confidently. Verify wisely. Stay safe.");
    } catch {
      toast.error("Couldn't record your agreement. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#171426]/70 backdrop-blur-md p-4" data-testid="disclaimer-gate">
      <div className="bg-white w-full max-w-lg rounded-3xl overflow-hidden flex flex-col max-h-[90vh] sms-fade-up">
        <div className="px-6 py-5 text-white flex items-center gap-3" style={{ background: "linear-gradient(160deg,#5d8a52,#3f6238)" }}>
          <div className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
            <ShieldCheck size={22} />
          </div>
          <div>
            <h2 className="text-lg font-extrabold leading-tight">Please read before you begin</h2>
            <p className="text-sm text-white/75">Vittyyo Disclaimer &amp; Terms of Use</p>
          </div>
        </div>

        {!expanded ? (
          <div className="p-6">
            <p className="text-sm text-[#4a475c] leading-relaxed">
              Vittyyo is an <span className="font-bold text-[#171426]">educational and informational</span> tool. Its content is
              AI-generated and may occasionally be inaccurate, incomplete, or out of date. It is <span className="font-bold text-[#171426]">not</span> a
              substitute for professional advice. Always verify important information and follow proper safety procedures — you are responsible for your own decisions and actions.
            </p>
            <button
              data-testid="disclaimer-read-btn"
              onClick={() => setExpanded(true)}
              className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#5d8a52] border border-[#e0d8fb] rounded-2xl px-4 py-2.5 hover:bg-[#f6f3ff] transition-colors"
            >
              <FileText size={16} /> Read the full disclaimer
            </button>
          </div>
        ) : (
          <div ref={scrollRef} onScroll={onScroll} className="px-6 py-4 overflow-y-auto text-[#4a475c] text-sm leading-relaxed space-y-3" data-testid="disclaimer-full">
            <h3 className="text-base font-extrabold text-[#171426]">Vittyyo Disclaimer</h3>
            {DISCLAIMER_PARAGRAPHS.map((b, i) => {
              if (b.h) return <p key={i} className="font-bold text-[#171426]">{b.h}</p>;
              if (b.strong) return <p key={i} className="font-bold text-[#5d8a52]">{b.strong}</p>;
              if (b.sign) return <p key={i} className="italic text-[#6b6880]">{b.sign}</p>;
              return <p key={i}>{b.p}</p>;
            })}
          </div>
        )}

        <div className="px-6 py-4 border-t border-[#f0eef7] bg-[#faf9fd]">
          <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="disclaimer-agree-label">
            <input
              type="checkbox"
              data-testid="disclaimer-agree-checkbox"
              checked={agree}
              disabled={expanded && !scrolledToEnd}
              onChange={(e) => setAgree(e.target.checked)}
              className="mt-0.5 w-5 h-5 accent-[#4f7746] shrink-0 disabled:opacity-40"
            />
            <span className="text-sm text-[#4a475c]">
              I have read and agree to the Vittyyo Disclaimer &amp; Terms of Use. I understand the content is AI-generated,
              may contain errors, and is for educational purposes only — I accept full responsibility for verifying information and my own actions.
            </span>
          </label>
          {expanded && !scrolledToEnd && (
            <p className="text-xs text-[#b7b3c8] mt-1.5 ml-8">Scroll to the end of the disclaimer to enable agreement.</p>
          )}
          <button
            data-testid="disclaimer-agree-btn"
            onClick={accept}
            disabled={!agree || busy}
            className="sms-solid-btn w-full text-white font-bold py-3.5 rounded-2xl mt-4 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {busy && <Loader2 size={16} className="spin" />} I Agree — Continue
          </button>
        </div>
      </div>
    </div>
  );
}
